import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { ROUTES, VALIDATION_MESSAGES } from '@constants';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import PasswordInput from '@components/common/PasswordInput';
import FowBrand from '@components/common/FowBrand';
import toast from 'react-hot-toast';
import { Rocket, Star, TrendingUp, Award, Hand, ShieldCheck, Zap } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email(VALIDATION_MESSAGES.EMAIL_INVALID),
  password: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
});

const FEATURES = [
  { icon: ShieldCheck, title: 'Secure & Private',      desc: 'Your data is encrypted and never shared with third parties.' },
  { icon: Zap,         title: 'Instant Access',         desc: 'Jump right back into your personalized dashboard.' },
  { icon: TrendingUp,  title: 'Track Your Progress',    desc: 'Pick up exactly where you left off on your career journey.' },
  { icon: Award,       title: 'Expert Mentorship',      desc: 'Stay connected with your mentors and upcoming sessions.' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAuth();
  const selectedPlan = searchParams.get('plan');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  const onSubmit = async (data) => {
    try {
      const result = await login(data);
      
      if (result.type === 'auth/login/fulfilled') {
        toast.success('Login successful!');
        
        // Store selected plan in localStorage for payment handling
        if (selectedPlan) {
          localStorage.setItem('selected_plan', selectedPlan);
        }
        
        // Route based on user role
        const userRole = result.payload?.user?.role;
        
        if (userRole === 'mentor') {
          navigate(ROUTES.MENTOR_DASHBOARD);
        } else if (userRole === 'student') {
          navigate(ROUTES.HOME);
        } else if (userRole === 'admin') {
          navigate(ROUTES.ADMIN_DASHBOARD);
        } else {
          // Default fallback
          navigate(ROUTES.DASHBOARD);
        }
      } else if (result.type === 'auth/login/rejected') {
        const errorMessage = result.payload || 'Invalid email or password';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'An unexpected error occurred');
    }
  };

  return (
    <>
      <style>{`
        .login-root {
          position: fixed;
          inset: 0;
          display: flex;
          overflow: hidden;
        }

        /* ── LEFT PANEL ── */
        .login-left {
          width: 42%;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
          position: relative;
          padding: 40px 44px;
          background: var(--login-left-bg, #1e2a4a);
        }
        .dark .login-left { --login-left-bg: #111827; }

        .login-blob1 {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
          width: 320px; height: 320px; top: -80px; left: -80px;
          background: radial-gradient(circle, rgba(239,68,68,0.18) 0%, transparent 70%);
        }
        .login-blob2 {
          position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
          width: 280px; height: 280px; bottom: 40px; right: -60px;
          background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%);
        }
        .login-grid {
          position: absolute; inset: 0; pointer-events: none; opacity: 0.04;
          background-image: linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
          background-size: 36px 36px;
        }

        /* ── RIGHT PANEL ── */
        .login-right {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          background: #f8fafc;
        }
        .dark .login-right { background: #0f172a; }

        .login-right-inner {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          padding: 48px 40px;
        }

        /* ── Tablet ── */
        @media (max-width: 1023px) {
          .login-root { flex-direction: column; position: static; min-height: 100vh; overflow: visible; }
          .login-left { width: 100%; flex-shrink: unset; padding: 28px 24px; min-height: unset; }
          .login-left-features { display: none !important; }
          .login-left-testimonial { display: none !important; }
          .login-left-hero p { display: none; }
          .login-right { overflow: visible; }
          .login-right-inner { max-width: 100%; padding: 32px 24px; }
        }

        /* ── Mobile ── */
        @media (max-width: 639px) {
          .login-left { padding: 22px 18px; }
          .login-left h1 { font-size: 22px !important; }
          .login-right-inner { padding: 24px 16px; }
        }

        .login-feature-item { display: flex; align-items: flex-start; gap: 14px; }
        .login-feature-icon {
          width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .login-feature-item:hover .login-feature-icon { background: rgba(239,68,68,0.2); }

        .login-testimonial-card {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 20px;
        }

        .login-trust-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px; padding: 5px 14px; margin-bottom: 20px;
        }

        .login-divider {
          display: flex; align-items: center; gap: 12px; margin: 4px 0;
        }
        .login-divider-line {
          flex: 1; height: 1px; background: #e5e7eb;
        }
        .dark .login-divider-line { background: #1e293b; }
      `}</style>

      <div className="login-root">

        {/* ════════════════════════════════
            LEFT — Branding Panel
        ════════════════════════════════ */}
        <div className="login-left">
          <div className="login-blob1" />
          <div className="login-blob2" />
          <div className="login-grid" />

          {/* Logo */}
          <Link to="/" className="relative z-10 flex items-center gap-3 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer" style={{ zIndex: 50 }}>
            <div className="w-7 h-7 rounded-xl bg-secondary-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <Rocket size={14} className="text-white" />
            </div>
            <span className="text-3xl xl:text-4xl font-bold text-white tracking-tight"><FowBrand size="small" /></span>
          </Link>

          {/* Hero */}
          <div className="login-left-hero relative z-10 my-6">
            <div className="login-trust-badge">
              <Star size={12} className="text-secondary-400 fill-secondary-400" />
              <span className="text-xs font-semibold text-white/70 tracking-wide">50,000+ active learners</span>
            </div>
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-3" style={{ lineHeight: 1.2 }}>
              Welcome back to{' '}
              <span className="text-secondary-400">F.O.W</span>
            </h1>
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              Sign in to continue your journey. Your goals, mentors, and progress are waiting for you.
            </p>
          </div>

          {/* Features */}
          <div className="login-left-features relative z-10 space-y-4 flex-1">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="login-feature-item">
                <div className="login-feature-icon">
                  <Icon size={17} className="text-secondary-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{title}</p>
                  <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          {/* <div className="login-left-testimonial relative z-10 mt-6 flex-shrink-0">
            <div className="login-testimonial-card">
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-secondary-400 fill-secondary-400" />
                ))}
              </div>
              <p className="text-white/70 text-xs leading-relaxed italic mb-3">
                "Every time I log in, I discover something new. CareerPath keeps me accountable and motivated."
              </p>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  A
                </div>
                <div>
                  <p className="text-white text-xs font-semibold leading-tight">Arjun Mehta</p>
                  <p className="text-white/40 text-xs">Product Manager at Microsoft</p>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* ════════════════════════════════
            RIGHT — Login Form
        ════════════════════════════════ */}
        <div className="login-right">
          <div className="login-right-inner">

            {/* Mobile logo */}
            <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center flex-shrink-0">
                <Rocket size={15} className="text-white" />
              </div>
              <span className="text-base font-bold text-primary-700"><FowBrand size="small" /></span>
            </Link>

            {/* Heading */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <Hand size={20} className="text-secondary-600" />
                </div>
                <h2 className="text-2xl font-bold text-primary-700">Welcome Back</h2>
              </div>
              <p className="text-primary-500 text-sm mt-2 ml-0">
                {selectedPlan ? `Sign in to continue with ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} plan` : 'Sign in to continue your career journey'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="space-y-5">
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                error={errors.email?.message}
                fullWidth
                {...register('email')}
              />

              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                error={errors.password?.message}
                fullWidth
                {...register('password')}
              />

              {/* Remember me + Forgot password */}
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-secondary-500 border-gray-300 rounded focus:ring-secondary-400"
                  />
                  <span className="text-sm text-primary-600 select-none">Remember me</span>
                </label>
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  className="text-sm text-secondary-500 font-medium hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" variant="primary" fullWidth loading={loading} className="py-3 text-sm font-semibold">
                Sign In
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
              <p className="text-sm text-primary-500">
                Don't have an account?{' '}
                <Link
                  to={ROUTES.REGISTER}
                  className="text-secondary-500 font-semibold hover:underline"
                >
                  Sign up free
                </Link>
              </p>
            </div>

          </div>
        </div>

      </div>
    </>
  );
};

export default LoginPage;